import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import ReviewModeBanner from '../components/ReviewModeBanner';
import { 
  getMembership, 
  getMembershipPlans, 
  purchaseMembership, 
  isMembershipActive,
  getCreditTransactions,
  canPurchaseMembership,
} from '../utils/membership';
import {
  loadRazorpayScript,
  createRazorpayOrder,
  verifyRazorpayPayment,
  buildRazorpayPrefill,
  withRazorpayCheckoutConfig,
  pollRazorpayOrderStatus,
} from '../utils/razorpay';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

export default function MembershipPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const paymentCompletedRef = useRef(false);
  const [membership, setMembership] = useState(null);
  const [plans, setPlans] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paymentError, setPaymentError] = useState('');
  const [showTransactions, setShowTransactions] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [membershipData, plansData] = await Promise.all([
        getMembership(),
        getMembershipPlans(),
      ]);
      setMembership(membershipData);
      setPlans(plansData);
    } catch (error) {
      console.error('Failed to load membership data:', error);
      toast.error('Failed to load membership data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadTransactions = async () => {
    const txns = await getCreditTransactions();
    setTransactions(txns);
    setShowTransactions(true);
  };

  const membershipActive = isMembershipActive(membership);
  const canBuy = canPurchaseMembership();
  const membershipExpired = membership && !membershipActive && new Date(membership.expires_at) <= new Date();
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);

  const handleProceed = async () => {
    if (!selectedPlan || processing) {
      if (!selectedPlan) {
        toast.error('Pick a membership plan first');
      }
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    setPaymentError('');
    paymentCompletedRef.current = false;
    let stopPolling = null;

    try {
      setProcessing(true);
      await loadRazorpayScript();

      const order = await createRazorpayOrder({
        backendUrl,
        amount: selectedPlan.price,
        receipt: `mem-${selectedPlan.id}-${Date.now()}`,
      });

      const finalize = async (razorpayOrderId, razorpayPaymentId) => {
        const newMembership = await purchaseMembership(
          selectedPlan.id,
          razorpayOrderId,
          razorpayPaymentId,
        );
        setMembership(newMembership);
        toast.success('Membership activated successfully!');
        navigate('/profile');
      };

      const options = {
        key: order.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'Backyard',
        description: `${selectedPlan.name} membership`,
        order_id: order.order_id,
        prefill: buildRazorpayPrefill(),
        handler: async (response) => {
          if (paymentCompletedRef.current) return;
          paymentCompletedRef.current = true;
          stopPolling?.();
          try {
            await verifyRazorpayPayment({
              backendUrl,
              payload: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
            });
            await finalize(response.razorpay_order_id, response.razorpay_payment_id);
          } catch (error) {
            console.error('Payment verification failed:', error);
            setPaymentError('Payment verification failed. Please try again.');
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => { stopPolling?.(); setProcessing(false); },
        },
        theme: {
          color: '#E64B65',
        },
      };

      const razorpayInstance = new window.Razorpay(withRazorpayCheckoutConfig(options));
      razorpayInstance.on('payment.failed', (response) => {
        stopPolling?.();
        setPaymentError(response?.error?.description || 'Payment failed. Please try again.');
        setProcessing(false);
      });

      // UPI Collect rescue — see utils/razorpay.js
      stopPolling = pollRazorpayOrderStatus({
        backendUrl,
        orderId: order.order_id,
        onPaid: async (info) => {
          if (paymentCompletedRef.current) return;
          paymentCompletedRef.current = true;
          try { razorpayInstance.close(); } catch { /* already closed */ }
          try {
            await finalize(order.order_id, info.captured_payment_id);
          } catch (err) {
            console.error('Post-poll completion failed:', err);
            setPaymentError('Payment received but membership activation failed. Contact support.');
          } finally {
            setProcessing(false);
          }
        },
      });

      razorpayInstance.open();
    } catch (error) {
      stopPolling?.();
      console.error('Membership payment failed:', error);
      setPaymentError('Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562] mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading membership data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-nav text-[#2B3A67]" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <header className="px-6 pt-6">
        <button
          data-testid="membership-back-button"
          onClick={() => navigate('/profile')}
          className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100"
        >
          <i className="ph-bold ph-arrow-left text-xl"></i>
        </button>
        <h1 data-testid="membership-title" className="text-3xl font-extrabold mt-4">
          Memberships
        </h1>
        <p data-testid="membership-subtitle" className="text-sm text-gray-500">
          {canBuy ? 'Pick a yearly package and keep your perks rolling.' : 'Your membership and credits.'}
        </p>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
        {membership && (
          <section data-testid="membership-current-card" className="bg-white rounded-3xl p-6 shadow-md border border-gray-100">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-400">Current membership</p>
                <h2 data-testid="membership-current-title" className="text-2xl font-extrabold mt-2">
                  {membership.plan_name}
                </h2>
                <p data-testid="membership-current-status" className="text-sm text-gray-500 mt-1">
                  {membershipActive
                    ? `Active until ${formatDate(membership.expires_at)}`
                    : membershipExpired
                    ? 'Expired — renew to keep perks'
                    : 'Awaiting activation'}
                </p>
              </div>
              <span data-testid="membership-current-badge" className="text-xs font-bold px-3 py-1 rounded-full bg-[#2B3A67] text-white">
                {membershipActive ? 'Active' : membershipExpired ? 'Expired' : 'Pending'}
              </span>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span data-testid="membership-balance-label" className="text-gray-500">
                  Balance available
                </span>
                <span data-testid="membership-balance" className="font-bold">
                  {formatCurrency(membership.balance_remaining)} of {formatCurrency(membership.price)}
                </span>
              </div>
              <div data-testid="membership-progress" className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                <div
                  data-testid="membership-progress-fill"
                  className="h-full rounded-full bg-gradient-to-r from-[#E54562] to-[#3AAFA9]"
                  style={{
                    width: `${Math.min(100, Math.max(0, (membership.balance_remaining / membership.price) * 100))}%`,
                  }}
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                data-testid="membership-view-transactions"
                onClick={loadTransactions}
                className="text-sm text-[#3AAFA9] font-semibold hover:underline"
              >
                View Transaction History
              </button>
            </div>
          </section>
        )}

        {showTransactions && transactions.length > 0 && (
          <section className="bg-white rounded-3xl p-6 shadow-md border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Transaction History</h3>
              <button
                onClick={() => setShowTransactions(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ph-bold ph-x text-xl"></i>
              </button>
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {transactions.map((txn) => (
                <div key={txn.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-sm">{txn.description}</p>
                    <p className="text-xs text-gray-400">{formatDate(txn.created_at)}</p>
                  </div>
                  <span className={`font-bold ${txn.amount >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {txn.amount >= 0 ? '+' : ''}{formatCurrency(txn.amount)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {!canBuy && !membership && (
          <p data-testid="membership-unavailable" className="text-sm text-gray-500">
            Memberships aren't available in the iOS app.
          </p>
        )}

        {canBuy && (
        <section data-testid="membership-plans" className="space-y-4">
          {plans.map((plan) => {
            const isSelected = plan.id === selectedPlanId;
            const isCurrent = membershipActive && membership?.plan_id === plan.id;
            return (
              <div
                key={plan.id}
                data-testid={`membership-plan-${plan.id}`}
                className={`bg-white rounded-3xl p-6 shadow-md border ${
                  isSelected ? 'border-[#3AAFA9]' : 'border-gray-100'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <i className="ph-fill ph-sparkle text-[#E54562]"></i>
                      <h3 data-testid={`membership-plan-name-${plan.id}`} className="text-xl font-extrabold">
                        {plan.name}
                      </h3>
                    </div>
                    <p data-testid={`membership-plan-highlight-${plan.id}`} className="text-sm text-gray-500 mt-2">
                      {plan.highlight}
                    </p>
                  </div>
                  <div className="text-right">
                    <p data-testid={`membership-plan-price-${plan.id}`} className="text-2xl font-extrabold">
                      {formatCurrency(plan.price)}
                    </p>
                    <p data-testid={`membership-plan-validity-${plan.id}`} className="text-xs text-gray-400">
                      1 year validity
                    </p>
                  </div>
                </div>

                <ul className="mt-4 space-y-2">
                  {plan.perks.map((perk, perkIndex) => (
                    <li
                      key={`${plan.id}-perk-${perkIndex}`}
                      data-testid={`membership-plan-perk-${plan.id}-${perkIndex}`}
                      className="flex items-start gap-2 text-sm text-gray-600"
                    >
                      <span className="mt-1 h-2 w-2 rounded-full bg-[#3AAFA9]" />
                      {perk}
                    </li>
                  ))}
                </ul>

                <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <i className="ph-bold ph-wallet"></i>
                    Member balance reloads on purchase.
                  </div>
                  <button
                    data-testid={`membership-select-${plan.id}`}
                    onClick={() => setSelectedPlanId(plan.id)}
                    disabled={isCurrent}
                    className={`rounded-full px-4 py-2 font-bold text-xs ${
                      isSelected
                        ? 'bg-[#E54562] text-white'
                        : 'border-2 border-[#2B3A67] text-[#2B3A67]'
                    }`}
                  >
                    {isCurrent ? 'Current plan' : isSelected ? 'Selected' : 'Choose plan'}
                  </button>
                </div>
              </div>
            );
          })}
        </section>
        )}

        {canBuy && selectedPlan && (
          <section className="bg-white rounded-3xl p-6 shadow-md border border-gray-100">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div>
                <p data-testid="membership-checkout-label" className="text-xs text-gray-400 uppercase tracking-widest">
                  Payment
                </p>
                <h3 data-testid="membership-checkout-title" className="text-xl font-extrabold mt-2">
                  {selectedPlan.name}
                </h3>
                <p data-testid="membership-checkout-note" className="text-sm text-gray-500 mt-1">
                  You'll be redirected to our payment partner to complete purchase.
                </p>
              </div>
              <p data-testid="membership-checkout-price" className="text-2xl font-extrabold">
                {formatCurrency(selectedPlan.price)}
              </p>
            </div>
            <button
              data-testid="membership-proceed-payment"
              onClick={handleProceed}
              className="w-full py-4 rounded-2xl bg-[#E54562] text-white font-extrabold disabled:opacity-70"
              disabled={processing}
            >
              {processing ? 'Launching payment...' : 'Proceed to Payment'}
            </button>
            {paymentError && (
              <div
                data-testid="membership-payment-error"
                className="bg-red-50 border border-red-200 text-red-600 rounded-2xl p-3 text-xs font-semibold mt-3"
              >
                {paymentError}
              </div>
            )}

            {user?.review_mode && (
              <ReviewModeBanner
                disabled={processing}
                amountLabel={formatCurrency(selectedPlan.price)}
                onBypass={async () => {
                  try {
                    setProcessing(true);
                    const newMembership = await purchaseMembership(
                      selectedPlan.id,
                      'REVIEW_MODE',
                      'REVIEW_MODE',
                    );
                    setMembership(newMembership);
                    toast.success('Membership activated (App Review Mode).');
                    navigate('/profile');
                  } catch (err) {
                    setPaymentError(err?.response?.data?.detail || 'Bypass failed.');
                  } finally {
                    setProcessing(false);
                  }
                }}
              />
            )}
          </section>
        )}
      </div>
    </div>
  );
}
